import { useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { DeleteOutline, Edit, Autorenew, Devices, MoreVert, LockReset } from "@mui/icons-material";
import { Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { Data } from "../types";

import { TableWrapperStyle } from "./style";
import headCells from "./tableHeadCells";
import RenewDialog from "./renewDialog";

import ResetPasswordDialog from "@/pages/UserManagement/Institution/partials/ResetPasswordDialog";
import TableSkeleton from "@/components/app/TableSkeleton";
import { getComparator, Order } from "@/utils/table-utils";
import EnhancedTableHead from "@/components/app/EnhancedTableHeader";
import ConfirmationModal from "@/components/app/modalVariants/confirmation";
import { useDeleteInstitution, useUpdateInstitutionStatus, useRenewInstitutionContract } from "@/api/hooks/institution";
import { InstitutionStatus } from "@/api/types/institution";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import Loading from "@/components/app/Loading/tableLoading";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";

const STATUS_COLOR: Record<InstitutionStatus, "success" | "warning" | "error"> = {
    active: "success",
    suspended: "warning",
    expired: "error",
};

export default function InstitutionTable({
    data: rows,
    isLoading,
    isFetching,
}: {
    data: Data[],
    isLoading: boolean,
    isFetching: boolean,
}) {
    const navigate = useNavigate();
    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof Data>("updatedAt");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [confirmModalId, setConfirmModal] = useState<number | null>(null);
    const [renewModalId, setRenewModal] = useState<number | null>(null);
    const [resetPasswordRow, setResetPasswordRow] = useState<Data | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [menuRow, setMenuRow] = useState<Data | null>(null);

    const updateAccess = useCheckPermissionAccess(PagesAccess.INSTITUTION, OperationAccess.UPDATE);
    const deleteAccess = useCheckPermissionAccess(PagesAccess.INSTITUTION, OperationAccess.DELETE);

    const { mutate: deleteInstitution, isPending: isDeleting } = useDeleteInstitution();
    const { mutate: updateStatus } = useUpdateInstitutionStatus();
    const { mutate: renewContract, isPending: isRenewing } = useRenewInstitutionContract();

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleEdit = (id: number) => navigate(ROUTE_CONSTANTS.USER_MANAGEMENT.INSTITUTIONS.UPDATE.EDIT_BY_ID(id).ABSOLUTE);

    const handleDetails = (id: number) =>
        navigate(ROUTE_CONSTANTS.USER_MANAGEMENT.INSTITUTIONS.DETAILS.DETAILS_BY_ID(id).ABSOLUTE);

    const handleStatusChange = (id: number, event: SelectChangeEvent) => {
        updateStatus({ id, body: { status: event.target.value as InstitutionStatus } });
    };

    const handleClose = () => setConfirmModal(null);
    const handleDelete = () =>
        confirmModalId &&
        deleteInstitution(confirmModalId, {
            onSuccess: () => setConfirmModal(null),
        });

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: Data) => {
        setMenuAnchor(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuRow(null);
    };

    // eslint-disable-next-line no-unused-vars
    const handleMenuAction = (action: (row: Data) => void) => () => {
        if (menuRow) action(menuRow);

        handleMenuClose();
    };

    const handleResetPasswordClose = () => setResetPasswordRow(null);

    const handleRenewClose = () => setRenewModal(null);
    const handleRenewConfirm = (newEndDate: string) =>
        renewModalId &&
        renewContract(
            { id: renewModalId, body: { newEndDate } },
            {
                onSuccess: () => setRenewModal(null),
            }
        );

    const visibleRows = useMemo(() => [...rows].sort(getComparator(order, orderBy)), [order, orderBy, rows]);
    const paginatedRows = useMemo(
        () => visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [visibleRows, page, rowsPerPage]
    );

    return (
        <TableWrapperStyle className="table-style-wrapper" sx={{ width: "100%", marginTop: "2em" }}>
            <TableContainer sx={{ maxHeight: "700px" }}>
                <Table
                    stickyHeader
                    aria-labelledby="tableTitle"
                    className="table"
                    component="div"
                    size="medium"
                    sx={{ padding: ".5em" }}
                >
                    <EnhancedTableHead<Data>
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        rowCount={rows.length}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody component="div" sx={{ position: "relative" }}>
                        {isLoading && <TableSkeleton cells={headCells.map((item) => item.id)} />}
                        {!isLoading && !isFetching && paginatedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={headCells.length + 1}>No Data To Show</TableCell>
                            </TableRow>
                        )}
                        {isFetching && <Loading />}
                        {paginatedRows.map((row) => {
                            return (
                                <TableRow key={row.id} className="table-row" component="div" role="checkbox" tabIndex={-1}>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.id}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div" sx={{ width: "15%" }}>
                                        <span className="table-value">{row.name}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div" sx={{ width: "18%" }}>
                                        <span className="table-value">{row.email}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.maxConcurrentDevices}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div" sx={{ width: "12%" }}>
                                        <span className="table-value">
                                            {updateAccess ? (
                                                <Select
                                                    fullWidth
                                                    size="small"
                                                    value={row.status}
                                                    onChange={(e) => handleStatusChange(row.id, e)}
                                                >
                                                    <MenuItem value="active">Active</MenuItem>
                                                    <MenuItem value="suspended">Suspended</MenuItem>
                                                    <MenuItem value="expired">Expired</MenuItem>
                                                </Select>
                                            ) : (
                                                <Chip color={STATUS_COLOR[row.status]} label={row.status} size="small" />
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.startDate}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{row.endDate}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">
                                            {dayjs(row.updatedAt).format("YYYY-MM-DD HH:mm")}
                                        </span>
                                    </TableCell>
                                    <TableCell align="right" className="table-cell" component="div">
                                        <span className="table-value" style={{ padding: "5px", justifyContent: "flex-end" }}>
                                            <IconButton
                                                aria-label="More actions"
                                                title="More actions"
                                                onClick={(event) => handleMenuOpen(event, row)}
                                            >
                                                <MoreVert />
                                            </IconButton>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={visibleRows.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <Menu
                anchorEl={menuAnchor}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                open={Boolean(menuAnchor)}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleMenuAction((row) => handleDetails(row.id))}>
                    <ListItemIcon>
                        <Devices fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Devices</ListItemText>
                </MenuItem>
                <MenuItem disabled={!updateAccess} onClick={handleMenuAction((row) => setRenewModal(row.id))}>
                    <ListItemIcon>
                        <Autorenew fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Renew Contract</ListItemText>
                </MenuItem>
                <MenuItem disabled={!updateAccess} onClick={handleMenuAction((row) => setResetPasswordRow(row))}>
                    <ListItemIcon>
                        <LockReset fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Reset Password</ListItemText>
                </MenuItem>
                <MenuItem disabled={!updateAccess} onClick={handleMenuAction((row) => handleEdit(row.id))}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem disabled={!deleteAccess} onClick={handleMenuAction((row) => setConfirmModal(row.id))}>
                    <ListItemIcon>
                        <DeleteOutline fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>
            <ResetPasswordDialog
                institutionId={resetPasswordRow?.id ?? null}
                institutionName={resetPasswordRow?.name}
                open={Boolean(resetPasswordRow)}
                onClose={handleResetPasswordClose}
            />
            <ConfirmationModal
                description="Are you sure you want to delete this institution?"
                handleClose={handleClose}
                loading={isDeleting}
                open={Boolean(confirmModalId)}
                onConfirm={handleDelete}
            />
            <RenewDialog
                loading={isRenewing}
                open={Boolean(renewModalId)}
                onClose={handleRenewClose}
                onConfirm={handleRenewConfirm}
            />
        </TableWrapperStyle>
    );
}
